import React, { useRef, useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { ThemeContext } from '../contexts/ThemeContext';
import {
  Chart,
  BarElement,
  LinearScale,
  CategoryScale, 
  Tooltip, 
  Legend,
  BarController
} from 'chart.js';

// Register Chart.js components
Chart.register(
  BarElement, 
  LinearScale, 
  CategoryScale, 
  Tooltip, 
  Legend,
  BarController
);

const DISTRIBUTION_EXPANDED_STORAGE_KEY = 'tradesDistributionExpanded';

const getDistributionColors = (bins) => {
  return bins.map((bin) => {
    const midpoint = (bin.bin_start + bin.bin_end) / 2;

    if (midpoint < -10) {
      return 'rgba(220, 53, 69, 0.7)';
    }

    if (midpoint < 0) {
      return 'rgba(255, 193, 7, 0.7)';
    }

    if (Math.abs(midpoint) < 1e-6) {
      return 'rgba(108, 117, 125, 0.7)';
    }

    if (midpoint <= 10) {
      return 'rgba(40, 167, 69, 0.7)';
    }

    return 'rgba(25, 135, 84, 0.7)';
  });
};

const calculateDistributionFromTrades = (trades = []) => {
  if (!Array.isArray(trades) || trades.length === 0) {
    return null;
  }

  const parsePercentage = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const closedTradePercentages = trades
    .filter((trade) => trade && trade.status === 'CLOSED')
    .map((trade) => {
      const primaryValue = parsePercentage(trade.final_gain_loss_pct);
      if (primaryValue !== null) {
        return primaryValue;
      }

      return parsePercentage(trade.total_gain_loss_pct);
    })
    .filter((value) => value !== null);

  if (closedTradePercentages.length === 0) {
    return null;
  }

  let minValue = Math.min(...closedTradePercentages);
  let maxValue = Math.max(...closedTradePercentages);
  let range = maxValue - minValue;

  let binCount = range === 0 ? 1 : 15;

  if (range > 0 && range < 10) {
    binCount = 10;
  } else if (range > 100) {
    binCount = 20;
  }

  const buffer = range > 0 ? range * 0.01 : Math.max(Math.abs(minValue) * 0.1, 1);
  let adjustedMin = minValue - buffer;
  let adjustedMax = maxValue + buffer;

  if (adjustedMin === adjustedMax) {
    adjustedMin -= 1;
    adjustedMax += 1;
  }

  const adjustedRange = adjustedMax - adjustedMin;
  const binSize = adjustedRange / binCount;

  const bins = Array.from({ length: binCount }, (_, index) => {
    const binStart = adjustedMin + index * binSize;
    const isLastBin = index === binCount - 1;
    const binEnd = isLastBin ? adjustedMax : adjustedMin + (index + 1) * binSize;

    const tradeCount = closedTradePercentages.filter((value) =>
      isLastBin ? value >= binStart && value <= binEnd : value >= binStart && value < binEnd
    ).length;

    return {
      bin_start: Number(binStart.toFixed(2)),
      bin_end: Number(binEnd.toFixed(2)),
      bin_label: `${binStart.toFixed(1)}% to ${binEnd.toFixed(1)}%`,
      trade_count: tradeCount,
      percentage: Number(((tradeCount / closedTradePercentages.length) * 100).toFixed(2))
    };
  });

  return {
    bins,
    labels: bins.map((bin) => bin.bin_label),
    counts: bins.map((bin) => bin.trade_count),
    colors: getDistributionColors(bins),
    totalTrades: closedTradePercentages.length
  };
};

function TradeGainLossDistribution({ trades, loading = false, periodLabel }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const distributionData = useMemo(() => calculateDistributionFromTrades(trades), [trades]);
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const storedValue = localStorage.getItem(DISTRIBUTION_EXPANDED_STORAGE_KEY);
    return storedValue === null ? false : storedValue === 'true';
  }); // New state for collapsible functionality - collapsed by default
  const { effectiveTheme } = useContext(ThemeContext);

  // Get theme-aware colors
  const getThemeColors = useCallback(() => {
    const isDarkMode = effectiveTheme === 'dark';
    return {
      textColor: isDarkMode ? '#e0e0e0' : '#212529',
      backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      gridColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
    };
  }, [effectiveTheme]);

  // Clean up chart instance when component unmounts
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  // Create or update chart when distribution data changes, theme changes, or expand/collapse
  useEffect(() => {
    if (!isExpanded) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      return;
    }

    if (!chartRef.current || !distributionData) {
      return;
    }

    // Destroy previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    try {
      const ctx = chartRef.current.getContext('2d');
      const themeColors = getThemeColors();
      
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: distributionData.labels,
          datasets: [
            {
              label: 'Number of Transactions',
              data: distributionData.counts,
              backgroundColor: distributionData.colors,
              borderColor: distributionData.colors.map(color => color.replace('0.7', '1')),
              borderWidth: 1,
              barPercentage: 0.5,
              categoryPercentage: 0.7
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: themeColors.backgroundColor,
              titleColor: themeColors.textColor,
              bodyColor: themeColors.textColor,
              borderColor: themeColors.borderColor,
              borderWidth: 1,
              callbacks: {
                title: (context) => {
                  const binIndex = context[0].dataIndex;
                  const bin = distributionData.bins[binIndex];
                  return `Gain/Loss Range: ${bin.bin_start.toFixed(1)}% to ${bin.bin_end.toFixed(1)}%`;
                },
                label: (context) => {
                  const count = context.raw;
                  const percentage = ((count / distributionData.totalTrades) * 100).toFixed(1);
                  return `${count} transaction${count !== 1 ? 's' : ''} (${percentage}%)`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Gain % Value Range',
                color: themeColors.textColor,
                font: {
                  weight: 'bold'
                }
              },
              ticks: {
                color: themeColors.textColor,
                maxRotation: 45,
                minRotation: 45
              },
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Transactions',
                color: themeColors.textColor,
                font: {
                  weight: 'bold'
                }
              },
              ticks: {
                color: themeColors.textColor,
                stepSize: 1,
                callback: (value) => {
                  return Number.isInteger(value) ? value : '';
                }
              },
              grid: {
                color: themeColors.gridColor
              }
            }
          }
        }
      });
    } catch (error) {
      console.error("Error creating distribution chart:", error);
    }
  }, [distributionData, effectiveTheme, isExpanded, getThemeColors]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(DISTRIBUTION_EXPANDED_STORAGE_KEY, isExpanded);
    }
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  const showSummary = !isExpanded && !loading && distributionData;

  return (
    <div className="h-100">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center flex-wrap">
          <button
            type="button"
            className="btn btn-link p-0 me-2 text-decoration-none"
            style={{ minWidth: 'auto' }}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
          </button>
          <span className="fs-5 mb-0 fw-bold">Distribution of Gains & Losses</span>
          {periodLabel && (
            <span className="text-muted ms-2">({periodLabel})</span>
          )}
          {isExpanded && loading && <Spinner animation="border" size="sm" className="ms-2" />}
        </div>
        {showSummary && (
          <div className="small text-muted">
            Total: {distributionData.totalTrades} closed trades
          </div>
        )}
      </div>
      {isExpanded && (
        <Card className="h-100">
          <Card.Body>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="text-center">
                  <Spinner animation="border" className="mb-2" />
                  <p className="text-muted mb-0">Loading distribution data...</p>
                </div>
              </div>
            ) : distributionData && distributionData.counts.length > 0 ? (
              <div style={{ height: '300px', position: 'relative' }}>
                <canvas ref={chartRef}></canvas>
              </div>
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="text-center text-muted">
                  <i className="bi bi-bar-chart fs-1 d-block mb-2"></i>
                  <p className="mb-0">
                    {loading
                      ? 'Loading distribution data...'
                      : 'No closed trades available for distribution analysis'}
                  </p>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default TradeGainLossDistribution; 